import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  TestBarEnableConfirmButtonForJobAction,
  TestBarForJobActionTypes,
  TestBarMoveToNextJobAction,
  TestBarPerformTasksBeforeMovingToNextJobAction
} from '@ispm/shared/store/actions/test-bar.actions';
import {catchError, filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {
  selectExecutionPathAsListItems, selectGetUserSelectionStatus,
  selectNodesNamesSuffixes,
  selectSelectedTreatmentActivityId
} from '../../../process-executor/store/selectors';
import { ProgressionService } from '@ispm/shared/services/progression.service';
import {
  selectChosenClinicalTrials,
  selectPathwayTestProgression,
  selectSelectedTreatmentPlan,
  selectSelectedTreatmentPlanNote
} from '../selectors';
import * as _ from 'lodash';
import { cloneDeep } from 'lodash';
import { from } from 'rxjs';
import { failedToSaveMessage, pathwaySelectionJobPurpose } from '../../models/constants';
import { PathwaysConfigurationService } from '../../services';
import {
  PathwayActionTypes,
  SavePathwaySelectionStateAction,
  SuccessfullySavedPathwaySelectionStateAction, UpdateFinalProgressionAction
} from '../../store/actions';
import { of } from 'rxjs/observable/of';
import { get, isEmpty } from 'lodash';
import { SetErrorAction } from '@ispm/shared/store/actions/error.actions';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { selectActiveJobInputByKey } from '@ispm/workflow/store/workflow-config.selectors';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { UpdateUserSelectionStatusAction } from '@ispm-client/modules/process-executor';
import { USER_SELECTION_STATUS, UserClicksElementsActionTypes } from '@ispm-client/modules/process-executor/store';
import { getTestButtonConfirmButtonEnabledState } from '@ispm/test/store/test-bar.selectors';
import { combineLatest } from 'rxjs/observable/combineLatest';
import {Progression} from "@ispm-shared/modules/common";

@Injectable()
export class CompletePathwaySelectionEffects {
    constructor(private actions$: Actions,
                private injector: Injector,
                private store$: Store<any>) {
    }

  private userSelectionStatus$ = this.store$.pipe(select(selectGetUserSelectionStatus));
  private selectedTreatment$ = this.store$.pipe(select(selectSelectedTreatmentActivityId));
  private enablingState$ = combineLatest([this.userSelectionStatus$, this.selectedTreatment$]);

  private completedActions = 0;
  private expectedCompletedActions = 2;

  @Effect()
  enableSelectPathwayCompletionEffect$ = this.enablingState$.pipe(
    filter(([userSelectionStatus, selectedTreatment]) =>
      userSelectionStatus === USER_SELECTION_STATUS.NotInProgress && !isEmpty(selectedTreatment)),
    switchMap(() => [new TestBarEnableConfirmButtonForJobAction({ enable: true, jobPurpose: pathwaySelectionJobPurpose})]));

  @Effect()
  savePathwaySelectionState$: Observable<Action> = this.actions$
    .pipe(
      ofType<TestBarPerformTasksBeforeMovingToNextJobAction>(TestBarForJobActionTypes.PerformTasksBeforeMovingToNextJob),
      filter(action => action.payload.jobPurpose === pathwaySelectionJobPurpose),
      map(action => {
          this.completedActions = 0;
          return new SavePathwaySelectionStateAction();
        }
      )
    );

  @Effect()
  saveExecutionHistory$: Observable<Action> = this.actions$
    .pipe(
      ofType<SavePathwaySelectionStateAction>(PathwayActionTypes.SAVE_PATHWAY_SELECTION_STATE),
      withLatestFrom(
        this.store$.pipe(select(selectExecutionPathAsListItems)),
        this.store$.pipe(select(selectSelectedTreatmentPlanNote)),
        this.store$.pipe(select(selectSelectedTreatmentPlan)),
        this.store$.pipe(select(selectSelectedTreatmentActivityId))
      ),
      tap(([action, executionPathListItems, selectedTreatmentPlanNote, selectSelectedTreatmentPlan, selectSelectedTreatmentActivityId]) =>
        this.checkExecutionHistoryAvailable(action, executionPathListItems, selectedTreatmentPlanNote, selectSelectedTreatmentPlan, selectSelectedTreatmentActivityId)
      ),
      switchMap(([action, executionPathListItems, selectedTreatmentPlanNote, selectSelectedTreatmentPlan, selectSelectedTreatmentActivityId]) =>
        this.getProgressionService().create('BpmnExecutionHistory', {
          'history': executionPathListItems,
          'tp-note': selectedTreatmentPlanNote,
          'selectedTreatment': {
            'NodeId': selectSelectedTreatmentActivityId,
            'Treatments': selectSelectedTreatmentPlan
          }
        })),
          map((res) => new SuccessfullySavedPathwaySelectionStateAction()),
          catchError(error => of(new SetErrorAction({
            error: error.message || error,
            devMessage: 'saveExecutionHistory$: Failed to create BpmnExecutionHistory Progression',
            userMessage: failedToSaveMessage
          })))
    );

  @Effect()
  saveNodesNames$: Observable<Action> = this.actions$
    .pipe(
      ofType<SavePathwaySelectionStateAction>(PathwayActionTypes.SAVE_PATHWAY_SELECTION_STATE),
      withLatestFrom(
        this.store$.pipe(select(selectNodesNamesSuffixes)),
        this.store$.pipe(select(selectPathwayTestProgression))
      ),
      switchMap(([action, nodesNamesSuffixes, progression]) =>
        this.getProgressionService().update(this.updateProgression(progression, nodesNamesSuffixes))
          .pipe(
            switchMap((progressionFromDB: Progression) => [
              new SuccessfullySavedPathwaySelectionStateAction(),
              new UpdateFinalProgressionAction({progression: progressionFromDB})]),
            catchError(error => of(new SetErrorAction({
              error: error.message,
              devMessage: 'saveNodesNames$: Failed to update pathway selection state',
              userMessage: failedToSaveMessage
            })))
          )
      )
    );

  @Effect()
  completePathwaySelection$: Observable<Action> = this.actions$
    .pipe(
      ofType<SuccessfullySavedPathwaySelectionStateAction>(PathwayActionTypes.SUCCESSFUlLY_SAVED_PATHWAY_SELECTION_STATE),
      filter(() => _.isEqual(++this.completedActions, this.expectedCompletedActions)),
      switchMap<any, any>((action) => {
        return forkJoin(
          Observable.of(action),
          this.store$.pipe(select(selectSelectedTreatmentPlan)).pipe(take(1)),
          this.store$.pipe(select(selectChosenClinicalTrials)).pipe(take(1)),
          this.getPathwaysConfigurationService().getBpmnLinkedResourcesType().pipe(take(1)),
          this.getPathwaysConfigurationService().getBpmnAttachedResourcesType().pipe(take(1)),
          this.getPathwaysConfigurationService().getTargetStepForPolling().pipe(take(1)),
          this.store$.pipe(select(selectActiveJobInputByKey(WorkflowConfigService.PROCESS_CONFIG_INPUT_TYPE)), take(1))
        );
      }),
      switchMap(([action, selectedTreatments, selectedClinicalTrials, bpmnLinkedResourcesType, bpmnAttachedResourcesType, targetStepForPolling, processConfigInput]) =>
        this.prepareResults(selectedTreatments, selectedClinicalTrials, bpmnLinkedResourcesType, bpmnAttachedResourcesType, targetStepForPolling, get(processConfigInput, 'args'))
      )
    );

  @Effect()
  disableSelectPathwayCompletionEffect$: Observable<Action> = this.actions$
    .pipe(
      ofType<UpdateUserSelectionStatusAction>(UserClicksElementsActionTypes.UPDATE_USER_SELECTION_STATUS),
      filter(action => action.payload.status === USER_SELECTION_STATUS.InProgress),
      withLatestFrom(this.store$.pipe(select(getTestButtonConfirmButtonEnabledState))),
      filter(([action, buttonState]) => buttonState === true),
      switchMap(() => [new TestBarEnableConfirmButtonForJobAction({ enable: false, jobPurpose: pathwaySelectionJobPurpose})])
    );

  private getPathwaysConfigurationService(): PathwaysConfigurationService {
    return this.injector.get(PathwaysConfigurationService);
  }

  private getProgressionService(): ProgressionService {
    return this.injector.get(ProgressionService);
  }

  private prepareResults(selectedTreatments, selectedClinicalTrials, bpmnLinkedResourcesType, bpmnAttachedResourcesType, targetStepForPolling?, flowConfig?) {
    let resultsTreatments = cloneDeep(selectedTreatments);
    if (resultsTreatments) {
      resultsTreatments.forEach(selectedTreatment => selectedTreatment.type = bpmnAttachedResourcesType);
    }
    else {
      resultsTreatments = [];
    }
    let resultsClinicalTrials = [];
    if (selectedClinicalTrials) {
      resultsClinicalTrials = cloneDeep(selectedClinicalTrials);
      resultsClinicalTrials.forEach(clinicalTrial => clinicalTrial.type = bpmnLinkedResourcesType);
    }
    let targetStep;
        if (flowConfig !== 'sync') {
          targetStep = targetStepForPolling ? targetStepForPolling : 'therapyPrioritization';
        }

    const actions = [
      new TestBarMoveToNextJobAction(
        {
          jobPurpose: pathwaySelectionJobPurpose,
          params: [...resultsTreatments, ...resultsClinicalTrials, {
            type: 'patient',
            id: this.getPathwaysConfigurationService().getPatientId()
          }],
          actionToDispatchOnError: {
            type: UserClicksElementsActionTypes.UPDATE_USER_SELECTION_STATUS,
            payload: USER_SELECTION_STATUS.NotInProgress
          },
          targetStepForPolling: targetStep,
                    flowConfig: flowConfig || 'async'
                })
        ];
        this.completedActions = 0;
        return from(actions);
    }

  private updateProgression(progression: any, nodesNamesSuffixes: any) {
    _.set(progression, 'data.additionalProperties.nodeIdsToNameSuffix', nodesNamesSuffixes);
    return progression;
  }

  private checkExecutionHistoryAvailable(action: any, executionPathListItems: any, selectedTreatmentPlanNote: any, selectSelectedTreatmentPlan: any, selectSelectedTreatmentActivityId: any) {
    if (isEmpty(executionPathListItems) || isEmpty(selectSelectedTreatmentActivityId)) {
      throw Error('saveNodesNames$: History not available for saving Progression');
    }
  }
}
